import React, { useState } from "react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Textarea } from "./ui/textarea";
import { useTranslation } from "react-i18next";
import { X, Check, ChevronDown } from "lucide-react";
import i18n from "../i18n";
import { pickPersonaField } from "../lib/persona-i18n";
import { localizePersonaOption } from "../lib/persona-options-i18n";
import {
  PERSONALITY_STYLE_CATEGORIES, PERSONALITY_CATEGORY_LABELS, PERSONALITY_STYLES,
  PERSONALITY_STYLE_BY_CODE, MATURITY_LEVELS, INTERNAL_PRESSURE_LEVELS,
  localizePersonalityStyle, localizeMaturityLevel, localizeInternalPressure, langKey,
  type PersonalityCategory,
} from "../lib/persona-personality";
import { pickStyleLabel, pickStyleDescription, type PersonalityStyleRow } from "../lib/personality-style-i18n";
import { personalityStylesApi } from "../lib/api";

// ── Constants ──

export const PERSONA_FUNCTIONS = [
  "Directeur Général / CEO", "Directeur Commercial", "Directeur Marketing",
  "Directeur Financier / CFO", "Directeur des Achats", "Directeur Technique / CTO",
  "Directeur des Ressources Humaines", "Directeur des Opérations / COO",
  "Responsable IT / DSI", "Chef de projet", "Responsable Qualité", "Responsable Logistique",
];

export const CSP_LIST = [
  "Agriculteur exploitant", "Artisan", "Commerçant", "Chef d'entreprise",
  "Cadre et profession intellectuelle supérieure", "Profession intermédiaire",
  "Employé", "Ouvrier", "Retraité", "Étudiant",
];

export const AGE_RANGES = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];
export const PURCHASE_CRITERIA = ["Prix", "Qualité", "Marque", "Avis", "Praticité"];
export const PURCHASE_PHASES = ["Réflexion", "Recherche d'infos", "Décision", "Déjà client"];
export const PRODUCT_RELATIONSHIPS = [
  "Problème ou besoin principal à résoudre",
  "Frustrations/points de douleur actuels",
  "Motivation d'achat (gain de temps, plaisir, statut, économie)",
  "Freins potentiels (prix, confiance, complexité)",
];

export const CONTACT_REASONS = ["Panne", "Dysfonctionnement", "Incompréhension produit/service", "Mécontentement livraison", "Demande de remboursement"];
export const EXPRESSION_STYLES = [
  { value: "Laconique", description: "Réponses courtes, peu de détails" },
  { value: "Bavard", description: "Parle beaucoup, digressions fréquentes" },
  { value: "Précis et technique", description: "Vocabulaire technique, attend des réponses précises" },
  { value: "Confus", description: "Difficulté à expliquer le problème, mélange les sujets" },
  { value: "Emotionnel", description: "Exprime frustration, colère ou déception" },
];
export const MAIN_EXPECTATIONS = ["Résolution rapide", "Explication", "Compensation", "Réassurance"];

export const DECISION_MAKER_CODES = [
  "decideurFinal", "decideurTechnique", "influenceur", "prescripteur",
  "champion", "utilisateurFinal", "nonDecideur", "gardien",
];

export const DECISION_MAKER_LABELS: Record<string, { fr: string; en: string }> = {
  decideurFinal: { fr: "Décideur final", en: "Final decision maker" },
  decideurTechnique: { fr: "Décideur technique", en: "Technical decision maker" },
  influenceur: { fr: "Influenceur", en: "Influencer" },
  prescripteur: { fr: "Prescripteur", en: "Prescriber" },
  champion: { fr: "Champion interne", en: "Internal champion" },
  utilisateurFinal: { fr: "Utilisateur final", en: "End user" },
  nonDecideur: { fr: "Non-décideur", en: "Non-decision maker" },
  gardien: { fr: "Gardien / Gatekeeper", en: "Gatekeeper" },
};

// ── Section "Psychologie" — référentiel partagé (cf. src/lib/persona-personality.ts) ──
// Ré-exporté ici pour conserver les sites d'import existants (`from "./PersonaUtils"`).
export {
  PERSONALITY_STYLE_CATEGORIES, PERSONALITY_CATEGORY_LABELS, PERSONALITY_STYLES,
  PERSONALITY_STYLE_BY_CODE, MATURITY_LEVELS, INTERNAL_PRESSURE_LEVELS,
  localizePersonalityStyle, localizeMaturityLevel, localizeInternalPressure,
};
export type { PersonalityCategory };

// ── Helpers ──

/**
 * Mappe une valeur (code OU libellé FR/EN) vers un code de profil décideur, sinon "".
 * L'agent IA renvoie souvent un libellé ("Décideur final") au lieu du code ("decideurFinal"),
 * ce qui laissait le select vide. On remappe ici.
 */
export function normalizeDecisionMakerCode(value?: string | null): string {
  if (!value) return "";
  const v = value.trim();
  if (DECISION_MAKER_CODES.includes(v)) return v;
  const lower = v.toLowerCase();
  for (const code of DECISION_MAKER_CODES) {
    const l = DECISION_MAKER_LABELS[code];
    if (l && (l.fr.toLowerCase() === lower || l.en.toLowerCase() === lower)) return code;
  }
  return "";
}

export function isCC(persona: any): boolean {
  return (persona.category || "sales") === "customer_care";
}

export function isB2C(persona: any): boolean {
  return (persona.personaType || persona.persona_type || "B2B") === "B2C";
}

/** Returns the display name for a persona */
export function getPersonaLabel(persona: any): string {
  // Priorité au titre optionnel s'il est renseigné (Mariam 10/07/26) — permet de
  // distinguer 2 personae de même fonction (ex : 2 CEO). Fallback sur la logique
  // métier existante si vide/null.
  const displayName = typeof persona?.displayName === "string" ? persona.displayName.trim()
    : (typeof persona?.display_name === "string" ? persona.display_name.trim() : "");
  if (displayName.length > 0) return displayName;
  // Lot 8 i18n — `function` est un champ HYBRIDE : soit une option de liste fermée
  // (« Directeur Marketing »…), soit une saisie libre traduite. On lit d'abord la
  // variante de langue (functionEn|Fr) puis on passe par localizePersonaOption :
  // pour une valeur de liste fermée legacy non retraduite, le dico fournit l'EN ;
  // pour une saisie custom ou déjà EN, la valeur est renvoyée inchangée (sans risque FR).
  const fn = localizePersonaOption(pickPersonaField(persona, "function", i18n.language), i18n.language);
  if (isCC(persona)) {
    return isB2C(persona)
      ? (localizePersonaOption(persona.contactReason || persona.contact_reason, i18n.language) || i18n.t("persona.fallbackCustomerCare"))
      : (fn || i18n.t("persona.fallbackCustomerCare"));
  }
  return isB2C(persona)
    ? (localizePersonaOption(persona.csp, i18n.language) || fn || i18n.t("persona.fallbackB2C"))
    : (fn || i18n.t("persona.fallbackGeneric"));
}

/** Localise un profil décideur (code → libellé FR/EN), tolère un libellé déjà saisi.
 * `langOverride` force la langue (sinon i18n.language courant). Exporté pour réutilisation
 * dans les écrans qui rendent un persona en résumé (wizards, cartes, briefs). */
export function localizeDecisionMaker(value: string | null | undefined, langOverride?: "fr" | "en"): string | undefined {
  if (!value) return undefined;
  const code = normalizeDecisionMakerCode(value);
  const lang = langOverride || (i18n.language?.toLowerCase().startsWith("en") ? "en" : "fr");
  return DECISION_MAKER_LABELS[code]?.[lang] || value;
}

/**
 * Affiche le profil décideur d'un persona en respectant la langue.
 * `decisionMakerProfile` est HYBRIDE :
 *  - soit un CODE (decideurFinal…) → traduit via DECISION_MAKER_LABELS ;
 *  - soit un TEXTE LIBRE FR (description du profil) → lu via la variante de langue
 *    (decisionMakerProfileEn|Fr), avec fallback sur le texte brut.
 * Prend le persona (et non la seule valeur) pour pouvoir lire les variantes *En/*Fr.
 */
export function pickDecisionMaker(persona: any, langOverride?: "fr" | "en"): string | undefined {
  const raw = persona?.decisionMakerProfile || persona?.decision_maker_profile;
  if (!raw) return undefined;
  const lang = langOverride || (i18n.language?.toLowerCase().startsWith("en") ? "en" : "fr");
  // Valeur de type CODE : traduction par libellé.
  if (normalizeDecisionMakerCode(raw)) return localizeDecisionMaker(raw, lang);
  // Texte libre : variante de langue (fallback legacy brut).
  return pickPersonaField(persona, "decisionMakerProfile", lang) || raw;
}

/**
 * Libellé de personnalité pour l'AFFICHAGE (liste / carte / détail), conscient du mode :
 *  - 'variable' → « Variable » (BizCoach fait tourner les styles) ;
 *  - 'fixed' avec 1-2 styles → les libellés des styles (ex. « Analytique / Autoritaire ») ;
 *  - sinon → repli sur le style legacy unique.
 * (Les fonctions d'affichage lisaient l'ancien champ unique, vide pour les personas du nouveau modèle.)
 */
export function getPersonaPersonalityLabel(persona: any, lang: string): string | null {
  const mode = persona.personalityMode || persona.personality_mode || "";
  const fixed = persona.fixedPersonalityStyles || persona.fixed_personality_styles || [];
  if (mode === "variable") return i18n.t("persona.personalityModeVariable");
  if (mode === "fixed" && Array.isArray(fixed) && fixed.length > 0) {
    return fixed.map((c: string) => localizePersonalityStyle(c, lang)?.label || c).join(" / ");
  }
  const code = persona.personalityStyle || persona.personality_style;
  return code ? (localizePersonalityStyle(code, lang)?.label || null) : null;
}

/**
 * Style de personnalité en DEUX niveaux (catégorie DISC › style), pour un affichage
 * détaillé : `variable` si BizCoach fait tourner les styles, sinon les styles fixés
 * (jusqu'à 2) ou, à défaut, le style legacy unique. Liste vide = aucun style défini.
 */
export function getPersonaPersonalityLevels(
  persona: any,
  lang: string,
): { variable: boolean; styles: Array<{ category: string; style: string }> } {
  const mode = persona.personalityMode || persona.personality_mode || "";
  if (mode === "variable") return { variable: true, styles: [] };
  const fixed = persona.fixedPersonalityStyles || persona.fixed_personality_styles || [];
  const legacy = persona.personalityStyle || persona.personality_style;
  const codes: string[] = mode === "fixed" && Array.isArray(fixed) && fixed.length > 0 ? fixed : legacy ? [legacy] : [];
  const lk = langKey(lang);
  return {
    variable: false,
    styles: codes.map((code) => {
      const entry = PERSONALITY_STYLE_BY_CODE[code];
      return {
        category: entry ? PERSONALITY_CATEGORY_LABELS[entry.category][lk] : "",
        style: localizePersonalityStyle(code, lang)?.label || code,
      };
    }),
  };
}

/** Returns the subtitle (secondary info) for a persona */
export function getPersonaSubtitle(persona: any): string {
  const styleLabel = getPersonaPersonalityLabel(persona, i18n.language?.toLowerCase().startsWith("fr") ? "fr" : "en");
  if (isCC(persona)) {
    const parts = [
      !isB2C(persona) ? pickDecisionMaker(persona) : null,
      localizePersonaOption(persona.contactReason || persona.contact_reason, i18n.language),
      // Transition : Style de personnalité si présent, sinon ancien Style d'expression.
      styleLabel || localizePersonaOption(persona.expressionStyle || persona.expression_style, i18n.language),
      localizePersonaOption(persona.mainExpectation || persona.main_expectation, i18n.language),
    ];
    return parts.filter(Boolean).join(" • ");
  }
  if (isB2C(persona)) {
    return [localizePersonaOption(persona.csp, i18n.language), persona.ageRange || persona.age_range].filter(Boolean).join(" • ");
  }
  // Lot 8 i18n — painPoints traduit (fallback legacy).
  return [pickDecisionMaker(persona), pickPersonaField(persona, "painPoints", i18n.language)].filter(Boolean).join(" • ");
}

/** Returns detail fields for the expand view */
export function getPersonaDetailFields(persona: any, isFr: boolean): Array<{ label: string; value: string | undefined }> {
  const t = i18n.t.bind(i18n);
  const lang = isFr ? "fr" : "en";
  const styleLabel = getPersonaPersonalityLabel(persona, lang) || undefined;
  if (isCC(persona)) {
    return [
      ...(!isB2C(persona) ? [{ label: t("persona.decisionMaker"), value: pickDecisionMaker(persona, lang) }] : []),
      { label: t("persona.contactReason"), value: localizePersonaOption(persona.contactReason, lang) },
      { label: t("persona.personalityStyle"), value: styleLabel },
      // expressionStyle conservé visible en détail pendant la transition.
      { label: t("persona.expressionStyle"), value: localizePersonaOption(persona.expressionStyle || persona.expression_style, lang) },
      { label: t("persona.mainExpectation"), value: localizePersonaOption(persona.mainExpectation, lang) },
    ];
  }
  if (isB2C(persona)) {
    return [
      { label: t("persona.csp"), value: localizePersonaOption(persona.csp, lang) },
      { label: t("persona.ageRange"), value: persona.ageRange },
      { label: t("persona.decisionCriteria"), value: localizePersonaOption(persona.purchaseCriteria, lang) },
      { label: t("persona.purchasePhase"), value: localizePersonaOption(persona.purchasePhase, lang) },
      { label: t("persona.productRelationship"), value: localizePersonaOption(persona.productRelationship, lang) },
    ];
  }
  // B2B Sales : Réalisme + Psychologie.
  return [
    { label: t("persona.decisionMaker"), value: pickDecisionMaker(persona, lang) },
    // Lot 8 i18n — champs texte libre traduits (fallback legacy).
    { label: t("persona.painPoints"), value: pickPersonaField(persona, "painPoints", lang) || undefined },
    { label: t("persona.personalMotivations"), value: pickPersonaField(persona, "personalMotivations", lang) || undefined },
    { label: t("persona.dailyIrritants"), value: pickPersonaField(persona, "dailyIrritants", lang) || undefined },
    { label: t("persona.typicalObjections"), value: pickPersonaField(persona, "typicalObjections", lang) || undefined },
    { label: t("persona.neverDisclose"), value: pickPersonaField(persona, "neverDisclose", lang) || undefined },
    { label: t("persona.personalityStyle"), value: styleLabel },
    { label: t("persona.maturityLevel"), value: localizeMaturityLevel(persona.maturityLevel || persona.maturity_level, lang) || undefined },
    { label: t("persona.internalPressure"), value: localizeInternalPressure(persona.internalPressure || persona.internal_pressure, lang) || undefined },
  ];
}

/** Vrai si un champ texte est rempli (tolère camelCase/snake_case + variantes Fr/En). */
function textFilled(persona: any, camel: string, snake: string): boolean {
  return !!(
    persona[camel] || persona[`${camel}Fr`] || persona[`${camel}En`] ||
    persona[snake] || persona[`${snake}_fr`] || persona[`${snake}_en`]
  );
}

/** Validates a persona — returns true if all required fields for this combination are filled */
export function isPersonaComplete(persona: any): boolean {
  // Le Style de personnalité n'est PLUS obligatoire : il est devenu un défaut optionnel
  // (choisi/modifiable au lancement d'une session — objet « Styles de personnalité »,
  // migration 20260715-2056). On ne le compte donc plus dans la complétude.
  if (isCC(persona)) {
    const ccOk = !!(persona.contactReason && persona.mainExpectation);
    return isB2C(persona) ? ccOk : !!(ccOk && persona.function && persona.decisionMakerProfile);
  }
  if (isB2C(persona)) {
    return !!(persona.csp && persona.ageRange && persona.purchaseCriteria && persona.purchasePhase && persona.productRelationship);
  }
  // B2B Sales : Réalisme (function + décideur + Problématiques adressées + 3 textes) ET Psychologie
  // (maturité + pression). painPoints (« Problématiques adressées ») est requis et toujours visible.
  return !!(
    persona.function && persona.decisionMakerProfile &&
    textFilled(persona, "painPoints", "pain_points") &&
    textFilled(persona, "personalMotivations", "personal_motivations") &&
    textFilled(persona, "dailyIrritants", "daily_irritants") &&
    textFilled(persona, "typicalObjections", "typical_objections") &&
    (persona.maturityLevel || persona.maturity_level) &&
    (persona.internalPressure || persona.internal_pressure)
  );
}

/**
 * Vrai si un persona Service client est resté sur l'ancien expressionStyle sans
 * Style de personnalité (DISC) → à migrer. Sert à afficher le tag "À migrer".
 */
export function personaNeedsPersonalityMigration(persona: any): boolean {
  if (!isCC(persona)) return false;
  const hasOld = !!(persona.expressionStyle || persona.expression_style);
  // ⚠️ Depuis le chantier « styles de personnalité » (17/07/2026), le style ne vit plus
  // seulement dans le champ legacy `personalityStyle` : il peut venir du mode `variable`
  // (BizCoach fait tourner les styles) ou d'une liste `fixedPersonalityStyles`.
  // Ne tester que le champ legacy laissait le tag « À migrer » collé au persona QUOI QUE
  // fasse le client — il le mettait à jour, le tag restait.
  const styles = persona.fixedPersonalityStyles || persona.fixed_personality_styles || [];
  const mode = persona.personalityMode || persona.personality_mode;
  const hasNew =
    !!(persona.personalityStyle || persona.personality_style) ||
    (Array.isArray(styles) && styles.length > 0) ||
    mode === "variable";
  return hasOld && !hasNew;
}

/** Builds the data object to send to the API when copying/creating a persona */
export function buildPersonaCopyData(persona: any): Record<string, any> {
  return {
    function: persona.function || persona.csp || persona.contactReason || persona.contact_reason || "-",
    decisionMakerProfile: persona.decisionMakerProfile || persona.decision_maker_profile || undefined,
    painPoints: persona.painPoints || persona.pain_points || undefined,
    category: persona.category || "sales",
    personaType: persona.personaType || persona.persona_type || "B2B",
    csp: persona.csp || undefined,
    ageRange: persona.ageRange || persona.age_range || undefined,
    gender: persona.gender || undefined,
    purchaseCriteria: persona.purchaseCriteria || persona.purchase_criteria || undefined,
    purchasePhase: persona.purchasePhase || persona.purchase_phase || undefined,
    productRelationship: persona.productRelationship || persona.product_relationship || undefined,
    contactReason: persona.contactReason || persona.contact_reason || undefined,
    expressionStyle: persona.expressionStyle || persona.expression_style || undefined,
    mainExpectation: persona.mainExpectation || persona.main_expectation || undefined,
    personalMotivations: persona.personalMotivations || persona.personal_motivations || persona.personalMotivationsFr || persona.personal_motivations_fr || undefined,
    dailyIrritants: persona.dailyIrritants || persona.daily_irritants || persona.dailyIrritantsFr || persona.daily_irritants_fr || undefined,
    typicalObjections: persona.typicalObjections || persona.typical_objections || persona.typicalObjectionsFr || persona.typical_objections_fr || undefined,
    neverDisclose: persona.neverDisclose || persona.never_disclose || persona.neverDiscloseFr || persona.never_disclose_fr || undefined,
    personalityStyle: persona.personalityStyle || persona.personality_style || undefined,
    personalityMode: persona.personalityMode || persona.personality_mode || "variable",
    fixedPersonalityStyles: persona.fixedPersonalityStyles || persona.fixed_personality_styles || [],
    maturityLevel: persona.maturityLevel || persona.maturity_level || undefined,
    internalPressure: persona.internalPressure || persona.internal_pressure || undefined,
  };
}

/** Maps a raw persona from API to a normalized local object */
export function mapPersonaFromApi(p: any, targetId: string): Record<string, any> {
  return {
    // Préserve les variantes i18n (*Fr/*En) que les helpers de lecture attendent.
    ...p,
    id: p.id,
    targetId,
    function: p.function || "",
    decisionMakerProfile: p.decisionMakerProfile || p.decision_maker_profile || "",
    painPoints: p.painPoints || p.pain_points || "",
    category: p.category || "sales",
    personaType: p.personaType || p.persona_type || "B2B",
    csp: p.csp || "",
    ageRange: p.ageRange || p.age_range || "",
    gender: p.gender || "",
    purchaseCriteria: p.purchaseCriteria || p.purchase_criteria || "",
    purchasePhase: p.purchasePhase || p.purchase_phase || "",
    productRelationship: p.productRelationship || p.product_relationship || "",
    contactReason: p.contactReason || p.contact_reason || "",
    expressionStyle: p.expressionStyle || p.expression_style || "",
    mainExpectation: p.mainExpectation || p.main_expectation || "",
    // Réalisme — textes libres (fallback Fr/En pour que la complétude se calcule bien).
    personalMotivations: p.personalMotivations || p.personal_motivations || p.personalMotivationsFr || p.personal_motivations_fr || p.personalMotivationsEn || p.personal_motivations_en || "",
    dailyIrritants: p.dailyIrritants || p.daily_irritants || p.dailyIrritantsFr || p.daily_irritants_fr || p.dailyIrritantsEn || p.daily_irritants_en || "",
    typicalObjections: p.typicalObjections || p.typical_objections || p.typicalObjectionsFr || p.typical_objections_fr || p.typicalObjectionsEn || p.typical_objections_en || "",
    neverDisclose: p.neverDisclose || p.never_disclose || p.neverDiscloseFr || p.never_disclose_fr || p.neverDiscloseEn || p.never_disclose_en || "",
    // Psychologie — codes de liste.
    personalityStyle: p.personalityStyle || p.personality_style || "",
    personalityMode: p.personalityMode || p.personality_mode || "variable",
    fixedPersonalityStyles: p.fixedPersonalityStyles || p.fixed_personality_styles || [],
    maturityLevel: p.maturityLevel || p.maturity_level || "",
    internalPressure: p.internalPressure || p.internal_pressure || "",
  };
}

// ── Display Components ──

/** Persona badges (B2B/B2C + Sales/SAV) */
export function PersonaBadges({ persona, size = "default" }: { persona: any; size?: "default" | "small" }) {
  const cls = size === "small" ? "text-[9px]" : "text-[10px]";
  return (
    <>
      <Badge variant={isB2C(persona) ? "secondary" : "default"} className={cls}>{isB2C(persona) ? "B2C" : "B2B"}</Badge>
      <Badge variant="outline" className={cls}>{isCC(persona) ? "SAV" : "Sales"}</Badge>
    </>
  );
}

/** Persona detail view — shows all fields for the expand panel */
export function PersonaDetailView({ persona, isFr }: { persona: any; isFr: boolean }) {
  const { t } = useTranslation();
  const fields = getPersonaDetailFields(persona, isFr).filter(f => f.value);
  if (fields.length === 0) {
    return <p className="text-xs text-muted-foreground italic">{t("persona.noDetails")}</p>;
  }
  return (
    <>
      {fields.map((f, i) => (
        <div key={i}>
          <p className="text-xs font-medium text-muted-foreground mb-0.5">{f.label}</p>
          <p className="text-sm">{f.value}</p>
        </div>
      ))}
    </>
  );
}

/**
 * Select dépendant à 2 niveaux pour le Style de personnalité (catégorie DISC → style).
 * Partagé entre le formulaire compte et les wizards. La valeur stockée est le code du style ;
 * la catégorie est dérivée du code sélectionné.
 */
export function PersonalityStyleSelect({
  value,
  onChange,
  triggerClassName,
  styles: dbStyles,
}: {
  value?: string;
  onChange: (code: string) => void;
  triggerClassName?: string;
  // Optionnel : liste des styles depuis la table `personality_styles` (source de vérité).
  // Absent → repli sur le référentiel intégré (persona-personality.ts), rétro-compatible.
  styles?: PersonalityStyleRow[];
}) {
  const { t } = useTranslation();
  const lang = i18n.language?.toLowerCase().startsWith("fr") ? "fr" : "en";
  const useDb = Array.isArray(dbStyles) && dbStyles.length > 0;

  // Options { code, label, description } groupées par catégorie (DB si fournie, sinon référentiel).
  const optionsByCategory = React.useMemo(() => {
    const acc: Record<string, { code: string; label: string; description: string }[]> = {};
    if (useDb) {
      for (const s of dbStyles!) {
        const cat = s.category || "TRANSVERSE";
        (acc[cat] ||= []).push({ code: s.code, label: pickStyleLabel(s, i18n.language), description: pickStyleDescription(s, i18n.language) });
      }
    } else {
      for (const cat of PERSONALITY_STYLE_CATEGORIES) {
        acc[cat] = PERSONALITY_STYLES[cat].map((s) => ({ code: s.code, label: s[lang].label, description: s[lang].description }));
      }
    }
    return acc;
  }, [useDb, dbStyles, lang]);

  const codeIndex = React.useMemo(() => {
    const m: Record<string, { category: string; description: string }> = {};
    for (const [cat, list] of Object.entries(optionsByCategory)) for (const o of list) m[o.code] = { category: cat, description: o.description };
    return m;
  }, [optionsByCategory]);

  const derivedCategory = value ? (codeIndex[value]?.category as PersonalityCategory | undefined) : undefined;
  const [category, setCategory] = React.useState<PersonalityCategory | undefined>(derivedCategory);

  React.useEffect(() => {
    if (derivedCategory && derivedCategory !== category) setCategory(derivedCategory);
  }, [derivedCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const styleOptions = category ? (optionsByCategory[category] || []) : [];
  const selectedDescription = value ? (codeIndex[value]?.description || "") : "";
  // Catégories à afficher : ordre fixe de la taxonomie, uniquement celles ayant des options.
  const categoriesToRender = PERSONALITY_STYLE_CATEGORIES.filter((c) => (optionsByCategory[c]?.length || 0) > 0);

  return (
    <div className="space-y-1.5">
      {/* Les 2 niveaux (catégorie → style) côte à côte, repli sur écran étroit. */}
      <div className="flex flex-wrap gap-2">
        <div className="w-full sm:w-48">
          <Select
            value={category}
            onValueChange={(c) => {
              const cat = c as PersonalityCategory;
              setCategory(cat);
              // Réinitialise le style si l'ancien n'appartient pas à la nouvelle catégorie.
              if (value && codeIndex[value]?.category !== cat) onChange("");
            }}
          >
            <SelectTrigger className={triggerClassName}>
              <SelectValue placeholder={t("persona.personalityCategoryPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {categoriesToRender.map((c) => (
                <SelectItem key={c} value={c}>{PERSONALITY_CATEGORY_LABELS[c][lang]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {category && (
          <div className="w-full sm:w-48">
            <Select value={value || undefined} onValueChange={onChange}>
              <SelectTrigger className={triggerClassName}>
                <SelectValue placeholder={t("persona.personalityStylePlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {styleOptions.map((s) => (
                  <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      {selectedDescription && (
        <p className="text-xs text-muted-foreground italic">{selectedDescription}</p>
      )}
    </div>
  );
}

/** Select simple — Niveau de maturité sur le sujet. */
export function MaturityLevelSelect({ value, onChange, triggerClassName }: { value?: string; onChange: (code: string) => void; triggerClassName?: string }) {
  const { t } = useTranslation();
  const lang = i18n.language?.toLowerCase().startsWith("fr") ? "fr" : "en";
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className={triggerClassName}>
        <SelectValue placeholder={t("persona.maturityPlaceholder")} />
      </SelectTrigger>
      <SelectContent>
        {MATURITY_LEVELS.map((m) => (
          <SelectItem key={m.code} value={m.code}>{m[lang]}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Select simple — Pression interne ressentie. */
export function InternalPressureSelect({ value, onChange, triggerClassName }: { value?: string; onChange: (code: string) => void; triggerClassName?: string }) {
  const { t } = useTranslation();
  const lang = i18n.language?.toLowerCase().startsWith("fr") ? "fr" : "en";
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className={triggerClassName}>
        <SelectValue placeholder={t("persona.internalPressurePlaceholder")} />
      </SelectTrigger>
      <SelectContent>
        {INTERNAL_PRESSURE_LEVELS.map((p) => (
          <SelectItem key={p.code} value={p.code}>{p[lang]}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Badges de statut d'une fiche : "À compléter" (champs requis manquants) et "À migrer" (SAV sans Style de personnalité). */
export function PersonaStatusBadges({ persona }: { persona: any }) {
  const { t } = useTranslation();
  const needsMigration = personaNeedsPersonalityMigration(persona);
  const incomplete = !isPersonaComplete(persona);
  if (!needsMigration && !incomplete) return null;
  return (
    <>
      {needsMigration && (
        <Badge variant="outline" className="text-[10px] border-[#926B00] text-[#926B00]">{t("persona.tagToMigrate")}</Badge>
      )}
      {incomplete && !needsMigration && (
        <Badge variant="outline" className="text-[10px] border-[#C0423F] text-[#C0423F]">{t("persona.tagIncomplete")}</Badge>
      )}
    </>
  );
}

// Couleurs des 5 catégories DISC (niveau 1) — charte BizCoach. bg = pastille/repère,
// ink = texte contrasté (WCAG), tint = fond léger. Sert à coder la catégorie partout
// (carte du style retenu + en-tête d'accordéon), pour que le niveau 1 reste toujours lisible.
const PERSONALITY_CAT_COLORS: Record<string, { bg: string; ink: string; tint: string }> = {
  DOMINANT: { bg: "#F49390", ink: "#C0423F", tint: "#FDECEB" },
  INFLUENT: { bg: "#F6C847", ink: "#926B00", tint: "#FDF5DC" },
  STABLE: { bg: "#7CBF94", ink: "#1E7A3C", tint: "#E7F4EC" },
  CONSCIENCIEUX: { bg: "#A6D3F9", ink: "#2C5A87", tint: "#EAF3FD" },
  TRANSVERSE: { bg: "#988EF2", ink: "#5A4BBE", tint: "#EFEDFC" },
};
const PERSONALITY_CAT_NEUTRAL = { bg: "#CFD8E6", ink: "#384872", tint: "#EEF1F7" };

/**
 * Sélecteur des styles de personnalité FIXES d'un persona (mode « fixe »).
 * Le style est une combinaison à 2 niveaux : catégorie DISC (niveau 1) × style (niveau 2).
 * Les catégories sont présentées en ACCORDÉON (on ouvre une famille pour voir ses styles),
 * et chaque style retenu s'affiche en carte à 2 niveaux + description, codée par couleur de
 * catégorie. Sélection de 0 à 2 styles. Alimenté par la table `personality_styles`.
 */
export function FixedPersonalityStylesSelect({
  value,
  onChange,
  styles,
}: {
  value: string[];
  onChange: (codes: string[]) => void;
  styles: PersonalityStyleRow[];
}) {
  const { t } = useTranslation();
  const langK = i18n.language?.toLowerCase().startsWith("fr") ? "fr" : "en";
  const MAX = 2;

  const selected = (Array.isArray(value) ? value : []).slice(0, MAX);
  const atMax = selected.length >= MAX;
  const byCode = new Map((styles || []).map((s) => [s.code, s]));
  const cats = PERSONALITY_STYLE_CATEGORIES.filter((c) => (styles || []).some((s) => (s.category || "") === c));
  const catOf = (code: string) => byCode.get(code)?.category || "";
  const colorOf = (cat: string) => PERSONALITY_CAT_COLORS[cat] || PERSONALITY_CAT_NEUTRAL;

  // Accordéon mono-ouvert : par défaut la famille du 1er style retenu, sinon la 1re.
  const [openCat, setOpenCat] = React.useState<string | null>(
    () => (selected[0] ? catOf(selected[0]) : "") || cats[0] || null,
  );

  const toggle = (code: string) => {
    if (selected.includes(code)) onChange(selected.filter((c) => c !== code));
    else if (!atMax) onChange([...selected, code]);
  };

  return (
    <div className="space-y-3">
      {/* Styles retenus — cartes à 2 niveaux (catégorie + style) + description, codées couleur. */}
      <div className="space-y-2">
        {selected.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">{t("persona.fixedStylesEmpty")}</p>
        ) : selected.map((code) => {
          const s = byCode.get(code) || ({ code } as PersonalityStyleRow);
          const c = colorOf(catOf(code));
          return (
            <div key={code} className="grid grid-cols-[auto_1fr_auto] items-start gap-x-3 rounded-xl border p-3 pl-3.5"
              style={{ background: c.tint, borderColor: c.bg, borderLeftWidth: 5, borderLeftColor: c.bg }}>
              <span className="row-span-3 mt-0.5 h-6 w-6 rounded-md" style={{ background: c.bg }} />
              <span className="col-start-2 text-[11px] font-extrabold uppercase tracking-wider" style={{ color: c.ink }}>
                {s.category ? PERSONALITY_CATEGORY_LABELS[s.category as PersonalityCategory]?.[langK] || s.category : ""}
              </span>
              <span className="col-start-2 font-bold text-sm leading-tight">{pickStyleLabel(s, i18n.language)}</span>
              <span className="col-start-2 text-xs text-muted-foreground leading-snug mt-0.5">{pickStyleDescription(s, i18n.language)}</span>
              <button type="button" onClick={() => toggle(code)} aria-label={t("persona.fixedStylesRemove") as string}
                className="row-span-3 self-start rounded-md p-1.5 text-muted-foreground hover:bg-black/5 hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground">{t("persona.fixedStylesMaxHint")}</p>

      {/* Accordéon des catégories (niveau 1) → styles (niveau 2).
          En-têtes stylés en « boutons verre liquide » : dégradé + reflet interne, ombre
          portée douce, bordure teintée translucide, pastille brillante, soulèvement au survol. */}
      <div className="space-y-2.5">
        {cats.map((cat) => {
          const c = colorOf(cat);
          const items = (styles || []).filter((s) => (s.category || "") === cat);
          const nSel = items.filter((s) => selected.includes(s.code)).length;
          const open = openCat === cat;
          const headerStyle: React.CSSProperties = open
            ? {
                background: `linear-gradient(180deg, ${c.tint} 0%, ${c.tint} 100%)`,
                border: `1px solid ${c.bg}`,
                boxShadow: `inset 0 2px 5px ${c.bg}55, inset 0 1px 0 rgba(255,255,255,.6)`,
              }
            : {
                background: `linear-gradient(180deg, #FFFFFF 0%, ${c.tint} 100%)`,
                border: `1px solid ${c.bg}66`,
                boxShadow: `inset 0 1px 0 rgba(255,255,255,.9), 0 1px 1px rgba(56,72,114,.04), 0 6px 14px rgba(56,72,114,.09)`,
              };
          return (
            <div key={cat}>
              <button type="button" aria-expanded={open}
                onClick={() => setOpenCat(open ? null : cat)}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-left transition-transform duration-150 hover:-translate-y-px active:translate-y-0 motion-reduce:transform-none motion-reduce:transition-none"
                style={headerStyle}>
                <span className="h-4 w-4 rounded-full shrink-0"
                  style={{ background: `radial-gradient(circle at 32% 26%, rgba(255,255,255,.9), rgba(255,255,255,0) 55%), ${c.bg}`, boxShadow: "inset 0 -1px 2px rgba(0,0,0,.18), 0 1px 2px rgba(0,0,0,.12)" }} />
                <span className="font-bold text-sm" style={{ color: c.ink }}>
                  {PERSONALITY_CATEGORY_LABELS[cat][langK]}
                </span>
                <span className="ml-auto flex items-center gap-3">
                  {nSel > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-white px-2 py-0.5 rounded-full"
                      style={{ background: c.ink, boxShadow: "inset 0 1px 0 rgba(255,255,255,.35), 0 1px 2px rgba(0,0,0,.15)" }}>
                      <Check className="h-3 w-3" />{nSel}
                    </span>
                  )}
                  <span className="text-xs tabular-nums" style={{ color: `${c.ink}B0` }}>{t("persona.fixedStylesCategoryCount", { count: items.length })}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: c.ink }} />
                </span>
              </button>
              {open && (
                <div className="mt-2 rounded-xl p-1.5 space-y-1"
                  style={{ background: "#fff", border: `1px solid ${c.bg}44`, boxShadow: "inset 0 1px 3px rgba(56,72,114,.06)" }}>
                  {items.map((s) => {
                    const isSel = selected.includes(s.code);
                    const disabled = !isSel && atMax;
                    return (
                      <button key={s.code} type="button" disabled={disabled} onClick={() => toggle(s.code)}
                        className={`w-full grid grid-cols-[auto_1fr] items-start gap-x-3 gap-y-0.5 rounded-lg border px-3 py-2 text-left transition-colors ${disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-black/[0.03]"}`}
                        style={isSel ? { background: c.tint, borderColor: c.bg } : { borderColor: "transparent" }}>
                        <span className="row-span-2 mt-0.5 h-5 w-5 shrink-0 rounded-md border flex items-center justify-center"
                          style={isSel ? { background: c.ink, borderColor: c.ink } : { borderColor: "#CFD8E6" }}>
                          {isSel && <Check className="h-3 w-3 text-white" />}
                        </span>
                        <span className="col-start-2 font-bold text-sm leading-tight">{pickStyleLabel(s, i18n.language)}</span>
                        <span className="col-start-2 text-xs text-muted-foreground leading-snug">{pickStyleDescription(s, i18n.language)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Formulaire persona UNIQUE partagé — utilisé partout : onglet Personae, création d'un cas
 * (wizard) et modification d'un cas. Agnostique de la persistance : chaque appelant fournit
 * `onSave(form)` (mémoire ou API) et `onCancel()`. Rend les 4 combos B2B/B2C × Vente/SAV avec
 * tous les champs (Réalisme + Psychologie). Save gated par isPersonaComplete.
 *
 * painPoints (« Problématiques adressées ») : toujours visible et obligatoire pour B2B Sales.
 */
export function PersonaForm({ persona, caseType, category, onSave, onCancel }: {
  persona: any;
  caseType?: "B2B" | "B2C";
  category?: "sales" | "customer_care";
  onSave: (p: any) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<any>(() => ({
    ...persona,
    personaType: persona?.personaType || caseType || "B2B",
    category: persona?.category || category || "sales",
    // Comportement de style : nouveau persona → 'variable' par défaut ; édition → valeur du persona.
    personalityMode: persona?.personalityMode || persona?.personality_mode || "variable",
    fixedPersonalityStyles: persona?.fixedPersonalityStyles || persona?.fixed_personality_styles || [],
  }));
  const [customFnMode, setCustomFnMode] = useState(false);
  // Catalogue « Styles de personnalité » (table) — source unique partagée avec le
  // lancement et l'Admin. Repli silencieux sur le référentiel intégré si indisponible.
  const [personalityStylesList, setPersonalityStylesList] = useState<PersonalityStyleRow[]>([]);
  React.useEffect(() => {
    personalityStylesApi.list().then(setPersonalityStylesList).catch(() => setPersonalityStylesList([]));
  }, []);
  const isB2CForm = form.personaType === "B2C";
  const isCCForm = form.category === "customer_care";
  const update = (field: string, value: any) => setForm({ ...form, [field]: value });

  const functionField = (labelKey: string) => (
    <div className="space-y-1">
      <Label>{t(labelKey)}</Label>
      <Select value={PERSONA_FUNCTIONS.includes(form.function) ? form.function : (customFnMode || (form.function && !PERSONA_FUNCTIONS.includes(form.function))) ? "_custom" : ""} onValueChange={(v) => {
        if (v === "_custom") { setCustomFnMode(true); }
        else { setCustomFnMode(false); update("function", v); }
      }}>
        <SelectTrigger><SelectValue placeholder={t("account.selectPlaceholder")} /></SelectTrigger>
        <SelectContent>
          {PERSONA_FUNCTIONS.map((f) => <SelectItem key={f} value={f}>{t(`account.personaeFunctions.${f}`, f)}</SelectItem>)}
          <SelectItem value="_custom">{t("account.otherEnter")}</SelectItem>
        </SelectContent>
      </Select>
      {(customFnMode || (form.function && !PERSONA_FUNCTIONS.includes(form.function))) && (
        <Input value={form.function} onChange={(e) => update("function", e.target.value)} placeholder={t("account.enterFunction")} className="mt-1" />
      )}
    </div>
  );

  const decisionMakerField = (
    <div className="space-y-1">
      <Label>{t("account.decisionMakerProfile")}</Label>
      <Select value={form.decisionMakerProfile || ""} onValueChange={(v) => update("decisionMakerProfile", v)}>
        <SelectTrigger><SelectValue placeholder={t("account.selectPlaceholder")} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="decideurFinal">{t("account.decisionMakerShort")}</SelectItem>
          <SelectItem value="decideurTechnique">{t("account.decisionMakerTechnicalShort")}</SelectItem>
          <SelectItem value="influenceur">{t("account.influenceurShort")}</SelectItem>
          <SelectItem value="prescripteur">{t("account.prescripteurShort")}</SelectItem>
          <SelectItem value="champion">{t("account.championShort")}</SelectItem>
          <SelectItem value="utilisateurFinal">{t("account.utilisateurFinalShort")}</SelectItem>
          <SelectItem value="nonDecideur">{t("account.nonDecideurShort")}</SelectItem>
          <SelectItem value="gardien">{t("account.gardienShort")}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  const contactReasonField = (
    <div className="space-y-1">
      <Label>{t("account.contactReason")}</Label>
      <Select value={form.contactReason || ""} onValueChange={(v) => update("contactReason", v)}>
        <SelectTrigger><SelectValue placeholder={t("account.selectPlaceholder")} /></SelectTrigger>
        <SelectContent>{CONTACT_REASONS.map((r) => <SelectItem key={r} value={r}>{t(`account.contactReasonsOptions.${r}`, r)}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  const mainExpectationField = (
    <div className="space-y-1">
      <Label>{t("account.mainExpectation")}</Label>
      <Select value={form.mainExpectation || ""} onValueChange={(v) => update("mainExpectation", v)}>
        <SelectTrigger><SelectValue placeholder={t("account.selectPlaceholder")} /></SelectTrigger>
        <SelectContent>{MAIN_EXPECTATIONS.map((e) => <SelectItem key={e} value={e}>{t(`account.mainExpectationsOptions.${e}`, e)}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  const personalityMode = form.personalityMode === "fixed" ? "fixed" : "variable";
  const personalityStyleField = (
    <div className="space-y-2">
      <Label>{t("persona.personalityMode")}</Label>
      {/* Toggle segmenté : variable (BizCoach fait tourner) / fixe (1 à 2 styles alternés). */}
      <div className="flex gap-2">
        {(["variable", "fixed"] as const).map((m) => (
          <Button key={m} size="sm" type="button"
            className={personalityMode === m ? "bg-[#384872] text-white hover:bg-[#384872]/90" : ""}
            variant={personalityMode === m ? "default" : "outline"}
            onClick={() => update("personalityMode", m)}>
            {t(m === "variable" ? "persona.personalityModeVariable" : "persona.personalityModeFixed")}
          </Button>
        ))}
      </div>
      {personalityMode === "variable" ? (
        <p className="text-xs text-muted-foreground">{t("persona.personalityModeVariableHint")}</p>
      ) : (
        <FixedPersonalityStylesSelect
          value={Array.isArray(form.fixedPersonalityStyles) ? form.fixedPersonalityStyles : []}
          onChange={(codes) => update("fixedPersonalityStyles", codes)}
          styles={personalityStylesList}
        />
      )}
    </div>
  );

  const expressionStyleField = (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Label>{t("account.expressionStyle")}</Label>
        {personaNeedsPersonalityMigration(form) && (
          <Badge variant="outline" className="text-[10px] border-[#926B00] text-[#926B00]">{t("persona.tagToMigrate")}</Badge>
        )}
      </div>
      <Select value={form.expressionStyle || ""} onValueChange={(v) => update("expressionStyle", v)}>
        <SelectTrigger><SelectValue placeholder={t("account.selectPlaceholder")} /></SelectTrigger>
        <SelectContent>
          {EXPRESSION_STYLES.map((s) => (
            <SelectItem key={s.value} value={s.value}>
              <span className="font-medium">{t(`account.expressionStylesOptions.${s.value}`, s.value)}</span> <span className="text-muted-foreground text-xs">— {t(`account.expressionStylesOptions.${s.value}Desc`, s.description)}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-[11px] text-muted-foreground italic">{t("persona.expressionStyleTransitionNote")}</p>
    </div>
  );

  const sectionLabel = (key: string) => (
    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(key)}</Label>
  );

  return (
    <Card className="border-2 border-blue-200">
      <CardContent className="!py-4 space-y-4">
        {/* Type B2B / B2C + Category */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("account.clientType")}</Label>
            <div className="flex gap-2">
              {["B2B", "B2C"].map((tp) => (
                <Button key={tp} size="sm"
                  className={form.personaType === tp ? "bg-[#384872] text-white hover:bg-[#384872]/90" : ""}
                  variant={form.personaType === tp ? "default" : "outline"}
                  onClick={() => update("personaType", tp)}>{tp === "B2B" ? t("account.proB2B") : t("account.individualB2C")}</Button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("account.usageContext")}</Label>
            <div className="flex gap-2">
              {[{ value: "sales", label: t("account.contextSales") }, { value: "customer_care", label: t("account.contextCare") }].map((c) => (
                <Button key={c.value} size="sm"
                  className={form.category === c.value ? (c.value === "sales" ? "bg-[#7CBF94] text-white hover:bg-[#7CBF94]/90" : "bg-[#877BF2] text-white hover:bg-[#877BF2]/90") : ""}
                  variant={form.category === c.value ? "default" : "outline"}
                  onClick={() => update("category", c.value)}>{c.label}</Button>
              ))}
            </div>
          </div>
        </div>

        {/* Titre optionnel — pour distinguer 2 personas de même fonction (ex: 2 CEO).
            Purement UI, jamais envoyé à N8N. Cf. Mariam 10/07/26. */}
        <div className="space-y-1">
          <Label>{t("account.personaDisplayName")}</Label>
          <Input
            value={form.displayName || ""}
            onChange={(e) => update("displayName", e.target.value)}
            placeholder={t("account.personaDisplayNamePlaceholder")}
            maxLength={120}
          />
        </div>

        {/* ============ B2B VENTE ============ */}
        {!isB2CForm && !isCCForm && (
          <>
            <div className="flex flex-wrap gap-3">
              <div className="w-full sm:w-56">{functionField("account.functionPersona")}</div>
              <div className="w-full sm:w-56">{decisionMakerField}</div>
            </div>

            {/* LE RÉALISME — ordre : Problématiques adressées, Motivations, Irritants, Objections, Ne doit jamais divulguer */}
            <div className="space-y-3 pt-1">
              {sectionLabel("persona.sectionRealism")}
              <div className="space-y-1">
                <Label>{t("account.painPoints")}</Label>
                <Input value={form.painPoints || ""} onChange={(e) => update("painPoints", e.target.value)} placeholder={t("account.painPointsShortPlaceholder")} />
              </div>
              <div className="space-y-1">
                <Label>{t("persona.personalMotivations")}</Label>
                <Textarea value={form.personalMotivations || ""} onChange={(e) => update("personalMotivations", e.target.value)} placeholder={t("persona.personalMotivationsHint")} rows={2} />
              </div>
              <div className="space-y-1">
                <Label>{t("persona.dailyIrritants")}</Label>
                <Textarea value={form.dailyIrritants || ""} onChange={(e) => update("dailyIrritants", e.target.value)} placeholder={t("persona.dailyIrritantsHint")} rows={2} />
              </div>
              <div className="space-y-1">
                <Label>{t("persona.typicalObjections")}</Label>
                <Textarea value={form.typicalObjections || ""} onChange={(e) => update("typicalObjections", e.target.value)} placeholder={t("persona.typicalObjectionsHint")} rows={2} />
              </div>
              <div className="space-y-1">
                <Label>{t("persona.neverDisclose")}</Label>
                <Textarea value={form.neverDisclose || ""} onChange={(e) => update("neverDisclose", e.target.value)} placeholder={t("persona.neverDiscloseHint")} rows={2} />
              </div>
            </div>

            {/* ENRICHIR LA PSYCHOLOGIE DU PROSPECT */}
            <div className="space-y-3 pt-1">
              {sectionLabel("persona.sectionPsychology")}
              {personalityStyleField}
              <div className="flex flex-wrap gap-3">
                <div className="space-y-1 w-full sm:w-56">
                  <Label>{t("persona.maturityLevel")}</Label>
                  <MaturityLevelSelect value={form.maturityLevel} onChange={(v) => update("maturityLevel", v)} />
                </div>
                <div className="space-y-1 w-full sm:w-56">
                  <Label>{t("persona.internalPressure")}</Label>
                  <InternalPressureSelect value={form.internalPressure} onChange={(v) => update("internalPressure", v)} />
                </div>
              </div>
            </div>
          </>
        )}

        {/* ============ B2C VENTE ============ */}
        {isB2CForm && !isCCForm && (
          <>
            <div className="flex flex-wrap gap-3">
              <div className="space-y-1 w-full sm:w-56">
                <Label>{t("account.cspRequired")}</Label>
                <Select value={form.csp || ""} onValueChange={(v) => update("csp", v)}>
                  <SelectTrigger><SelectValue placeholder={t("account.selectPlaceholder")} /></SelectTrigger>
                  <SelectContent>{CSP_LIST.map((c) => <SelectItem key={c} value={c}>{t(`account.cspOptions.${c}`, c)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1 w-full sm:w-56">
                <Label>{t("account.ageRange")}</Label>
                <Select value={form.ageRange || ""} onValueChange={(v) => update("ageRange", v)}>
                  <SelectTrigger><SelectValue placeholder={t("account.selectPlaceholder")} /></SelectTrigger>
                  <SelectContent>{AGE_RANGES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="pt-1">{personalityStyleField}</div>

            {/* COMPORTEMENT D'ACHAT */}
            <div className="space-y-3 pt-1">
              {sectionLabel("account.purchaseBehavior")}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>{t("account.decisionCriteria")}</Label>
                  <Select value={form.purchaseCriteria || ""} onValueChange={(v) => update("purchaseCriteria", v)}>
                    <SelectTrigger><SelectValue placeholder={t("account.selectPlaceholder")} /></SelectTrigger>
                    <SelectContent>{PURCHASE_CRITERIA.map((c) => <SelectItem key={c} value={c}>{t(`account.purchaseCriteriaOptions.${c}`, c)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>{t("account.purchasePhase")}</Label>
                  <Select value={form.purchasePhase || ""} onValueChange={(v) => update("purchasePhase", v)}>
                    <SelectTrigger><SelectValue placeholder={t("account.selectPlaceholder")} /></SelectTrigger>
                    <SelectContent>{PURCHASE_PHASES.map((p) => <SelectItem key={p} value={p}>{t(`account.purchasePhasesOptions.${p}`, p)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>{t("account.productRelationship")}</Label>
                  <Select value={form.productRelationship || ""} onValueChange={(v) => update("productRelationship", v)}>
                    <SelectTrigger><SelectValue placeholder={t("account.selectPlaceholder")} /></SelectTrigger>
                    <SelectContent>{PRODUCT_RELATIONSHIPS.map((r) => <SelectItem key={r} value={r}>{t(`account.productRelationshipsOptions.${r}`, r)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ============ SERVICE CLIENT (B2B + B2C) ============ */}
        {isCCForm && (
          <>
            {!isB2CForm ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {functionField("account.interlocutorFunctionRequired")}
                {decisionMakerField}
                {contactReasonField}
                {mainExpectationField}
              </div>
            ) : (
              <div className="space-y-3">
                {sectionLabel("account.careProfile")}
                <div className="flex flex-wrap gap-3">
                  <div className="w-full sm:w-56">{contactReasonField}</div>
                  <div className="w-full sm:w-56">{mainExpectationField}</div>
                </div>
              </div>
            )}

            <div className="pt-1">{personalityStyleField}</div>
            {expressionStyleField}
          </>
        )}

        {/* Actions */}
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" size="sm" onClick={onCancel}>{t("common.cancel")}</Button>
          <Button size="sm" disabled={!isPersonaComplete(form)} onClick={() => onSave(form)}>{t("common.save")}</Button>
        </div>
      </CardContent>
    </Card>
  );
}
